import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import {
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipPayload,
  TooltipProps,
  ComposedChart,
  ReferenceArea,
  Line,
} from "recharts";
import pluralize from "pluralize";
import { FullToggle } from "../../../components/FullToggle";
import TooltipHelper from "../../../components/TooltipHelper";

interface DailyProps {
  data: App.DailyData[];
  dataKeys: [
    [keyof App.DailyElement, keyof App.DailyElement],
    [keyof App.DailyElement, keyof App.DailyElement],
    [keyof App.DailyElement, keyof App.DailyElement]
  ];
  title: string;
  barLabel: string;
  startPre?: string;
  color: string;
  darkColor?: string;
  showIndividualToggle?: boolean;
}

export default function Daily(props: DailyProps) {
  const {
    data,
    dataKeys,
    darkColor,
    title,
    barLabel,
    color,
    startPre,
    showIndividualToggle = false,
  } = props;

  const [deltaToggle, setDeltaToggle] = useState(false);
  // only used for test administered
  const [individualToggle, setIndividualToggle] = useState(false);

  function CustomTooltip({
    payload,
    label,
    active,
  }: {
    payload?: ReadonlyArray<TooltipPayload> | undefined;
    label?: string | number | undefined;
    active?: boolean | undefined;
  }) {
    if (active && payload && payload.length === 3) {
      const newEvents = payload[2].value as number;
      const oldEvents = payload[0].value as number;
      const movingAverage = payload[1].value as number;
      let totalDaily = oldEvents + newEvents;
      return (
        <div className="chart-tooltip">
          <h5 className="font-weight-normal mb-1">
            {barLabel === "Death"
              ? "Date of Death on "
              : barLabel === "Positive Case"
              ? "Positive Case Confirmed on "
              : "Specimen Collected on"}{" "}
            {label}
          </h5>
          <p className="font-weight-bold mb-2">
            Total Daily {pluralize(barLabel)}: {totalDaily}
          </p>
          <p className="mb-1">
            <span style={{ color: color }}>{"\u25A0"} </span>Newly Reported{" "}
            {pluralize(barLabel)}: {newEvents}
          </p>
          <p className="mb-0">
            <span style={{ color: "#ccc" }}>{"\u25A0"} </span>Existing Reported{" "}
            {pluralize(barLabel)}: {oldEvents}
          </p>
          <p className="mb-0">
            <span style={{ color: darkColor ? darkColor : color }}>
              {"\u2501"}{" "}
            </span>
            Moving Average ({pluralize(barLabel)}): {movingAverage}
          </p>
        </div>
      );
    } else if (active && payload && payload.length === 2) {
      const newEvents = payload[1].value as number;
      const oldEvents = payload[0].value as number;
      let totalDaily = oldEvents + newEvents;
      return (
        <div className="chart-tooltip">
          <h5 className="font-weight-normal mb-1">
            {barLabel === "Death"
              ? "Date of Death on "
              : barLabel === "Positive Case"
              ? "Positive Case Confirmed on "
              : "Specimen Collected on"}{" "}
            {label}
          </h5>
          <p className="font-weight-bold mb-2">
            Total Daily {pluralize(barLabel)}: {totalDaily}
          </p>
          <p className="mb-1">
            <span style={{ color: color }}>{"\u25A0"} </span>Newly Reported{" "}
            {pluralize(barLabel)}: {newEvents}
          </p>
          <p className="mb-0">
            <span style={{ color: "#ccc" }}>{"\u25A0"} </span>Existing Reported{" "}
            {pluralize(barLabel)}: {oldEvents}
          </p>
        </div>
      );
    } else if (active && payload && payload.length === 1) {
      return (
        <div className="chart-tooltip">
          <h5 className="font-weight-normal mb-1">
            {barLabel === "Death"
              ? "Date of Death on "
              : barLabel === "Positive Case"
              ? "Positive Case Confirmed on "
              : "Specimen Collected on"}{" "}
            {label}
          </h5>
          <p className="mb-1">
            <span style={{ color: color }}>{"\u25A0"} </span>Newly Reported{" "}
            {pluralize(barLabel)}: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  }

  function referenceLabel(props: any) {
    return (
      <text
        className="prelim-text"
        x={props.viewBox.x - 65}
        y={props.viewBox.y + 30}
      >
        prelim &rarr;
      </text>
    );
  }

  return (
    <div>
      <Row className="mb-3">
        <Col xs={12} md={6}>
          <div className="d-flex mb-0">
            <h5>{title}</h5>
            <TooltipHelper
              text={`The daily ${barLabel.toLocaleLowerCase()} average line is a 7-day moving average (6 previous days and 1 current day) of the bar values.`}
            />
          </div>
        </Col>
        <Col className="d-flex flex-column align-items-end" xs={12} md={6}>
          <FullToggle
            options={["All", "Newly Reported"]}
            checked={deltaToggle}
            setChecked={setDeltaToggle}
          />
          {showIndividualToggle ? (
            <div className="mt-2">
              <FullToggle
                options={["All Tests", "Individuals"]}
                checked={individualToggle}
                setChecked={setIndividualToggle}
              />
            </div>
          ) : null}
        </Col>
      </Row>
      <div id="new-cases-by-day" className="mb-4">
        <ResponsiveContainer height={300}>
          <ComposedChart
            data={data}
            margin={{
              top: 5,
              bottom: 0,
            }}
          >
            <XAxis
              dataKey="date"
              label={{
                value: "Date of " + barLabel,
                position: "insideBottom",
                dy: 17,
              }}
            />
            <YAxis />
            <Tooltip
              cursor={{ fill: "transparent" }}
              labelStyle={{ fontWeight: "bold", fontSize: "12px" }}
              itemStyle={{ color: "black", fontSize: "10px" }}
              wrapperStyle={{ backgroundColor: "white" }}
              content={(props: TooltipProps) => <CustomTooltip {...props} />}
            />
            <Legend
              wrapperStyle={{
                paddingTop: "10px",
                paddingLeft: "40px",
              }}
            />
            <Legend />
            {!deltaToggle ? (
              <Bar
                dataKey={dataKeys[1][individualToggle ? 0 : 1]}
                stackId="a"
                name={pluralize(barLabel) + " by Day"}
                barSize={20}
                fill="#ccc"
              ></Bar>
            ) : null}
            {!deltaToggle ? (
              <Line
                dataKey={dataKeys[2][individualToggle ? 0 : 1]}
                dot={false}
                legendType="plainline"
                name={`7-Day Moving Avg (${pluralize(barLabel)})`}
                strokeWidth="2"
                type="monotone"
                stroke={darkColor ? darkColor : color}
              />
            ) : null}
            <Bar
              dataKey={dataKeys[0][individualToggle ? 0 : 1]}
              stackId="a"
              name={"Newly Reported " + pluralize(barLabel)}
              barSize={20}
              fill={color}
            ></Bar>
            {startPre ? (
              <ReferenceArea
                x1={startPre}
                stroke={color}
                strokeOpacity={0.3}
                label={referenceLabel}
              />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
